import { brand } from 'config';

type FetchParams = [planType: string, isBigTicket?: boolean];

export type PlanLink = {
  url: string;
  label: string;
};

type PlanVariant = {
  name: string;
  primaryBenefit?: string;
};

type CommonPlanData = {
  headerName: string;
  additionalInfo: string;
  voucherBenefit: string;
  benefits: string[];
  contactInfo: {
    label: string;
  };
  termsAndConditions: PlanLink;
  worryNoMoreDetails?: PlanLink;
  FAQDetails?: PlanLink;
};

export type Plans = {
  protectionPlanDesc: {
    COMMON: CommonPlanData;
    plans: Record<string, PlanVariant>;
  };
};

export type ParsedPlan = Omit<Omit<CommonPlanData, 'contactInfo'>, 'worryNoMoreDetails'> & {
  contactInfo: string;
  detailsLink: PlanLink;
  plans?: Record<string, PlanVariant>;
};

function parseAEMPlans(data: Plans): ParsedPlan {
  const parsedCommonPlan = data?.protectionPlanDesc?.COMMON ?? {};
  const plans = data?.protectionPlanDesc?.plans ?? null;

  return {
    ...parsedCommonPlan,
    ...(!!plans && { plans }),
    detailsLink: (parsedCommonPlan?.worryNoMoreDetails ?? parsedCommonPlan?.FAQDetails) as PlanLink,
    contactInfo: parsedCommonPlan?.contactInfo?.label ?? '',
  };
}

const getUrl = (planType: string, isBigTicket: boolean): string =>
  `/addons/protectionplan/${isBigTicket ? 'bigticket' : 'smallticket'}/${planType}.json`;

const isMocked = (): boolean => {
  const mockParam = useRoute().query?.mock;

  return typeof mockParam === 'string' && mockParam.startsWith('worryNoMore');
};

function getFetchPromise(planType: string, isBigTicket: boolean = false): Promise<Plans> {
  if (isMocked()) return import(`../../../../mocks/${brand}/worryNoMoreAEM/${planType}.json`);
  return $fetch(getUrl(planType, isBigTicket));
}

export default async function fetchPlans(...planOpts: FetchParams): Promise<ParsedPlan | null> {
  try {
    const data: Plans = await getFetchPromise(...planOpts);
    return parseAEMPlans(data);
  } catch (e) {
    // silent error - error recorded in logs but not thrown
    console.error(e);
    return null;
  }
}
